// ---------------------------------------------------------------------------
// Custom event definitions for sysml-mfe
// ---------------------------------------------------------------------------

// Events EMITTED by sysml-mfe
export const SYSML_EVENTS = {
  READY: 'sysml-ready',
  STATUS: 'sysml-status',
  DOCUMENT_SAVED: 'sysml-document-saved',
  DOCUMENT_CHANGED: 'sysml-document-changed',
  PARSE_COMPLETE: 'sysml-parse-complete',
  ERROR: 'sysml-error',
  INGESTION_STARTED: 'sysml-ingestion-started',
  INGESTION_COMPLETE: 'sysml-ingestion-complete',
  INGESTION_ERROR: 'sysml-ingestion-error',
} as const;

// Events RECEIVED from host
export const SYSML_HOST_EVENTS = {
  REFRESH: 'sysml-refresh',
  THEME_CHANGE: 'sysml-theme-change',
  FOCUS: 'sysml-focus',
} as const;

// ---------------------------------------------------------------------------
// Typed payloads
// ---------------------------------------------------------------------------

export interface SysmlStatusPayload {
  type: 'success' | 'error' | 'warn' | 'info';
  text: string;
}

export interface SysmlParsePayload {
  success: boolean;
  partCount: number;
  errorCount: number;
  warningCount: number;
}

export interface SysmlDocumentSavedPayload {
  documentId: string;
  name: string;
}

export interface SysmlDocumentChangedPayload {
  documentId: string;
  name: string;
}

export interface SysmlErrorPayload {
  message: string;
  code?: string;
}

// ---------------------------------------------------------------------------
// Emit helpers (bubbles + composed for shadow DOM traversal)
// ---------------------------------------------------------------------------

function emit<T>(el: HTMLElement, name: string, detail?: T): void {
  el.dispatchEvent(
    new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail,
    }),
  );
}

export function emitReady(el: HTMLElement): void {
  emit(el, SYSML_EVENTS.READY);
}

export function emitStatus(el: HTMLElement, payload: SysmlStatusPayload): void {
  emit(el, SYSML_EVENTS.STATUS, payload);
}

export function emitParseComplete(el: HTMLElement, payload: SysmlParsePayload): void {
  emit(el, SYSML_EVENTS.PARSE_COMPLETE, payload);
}

export function emitDocumentSaved(el: HTMLElement, payload: SysmlDocumentSavedPayload): void {
  emit(el, SYSML_EVENTS.DOCUMENT_SAVED, payload);
}

export function emitDocumentChanged(el: HTMLElement, payload: SysmlDocumentChangedPayload): void {
  emit(el, SYSML_EVENTS.DOCUMENT_CHANGED, payload);
}

export function emitError(el: HTMLElement, payload: SysmlErrorPayload): void {
  emit(el, SYSML_EVENTS.ERROR, payload);
}

// ── Ingestion events ──────────────────────────────────────────────────────

export interface SysmlIngestionStartedPayload {
  filename: string;
}

export interface SysmlIngestionCompletePayload {
  filename: string;
  requirementCount: number;
}

export interface SysmlIngestionErrorPayload {
  message: string;
  filename?: string;
}

export function emitIngestionStarted(el: HTMLElement, payload: SysmlIngestionStartedPayload): void {
  emit(el, SYSML_EVENTS.INGESTION_STARTED, payload);
}

export function emitIngestionComplete(el: HTMLElement, payload: SysmlIngestionCompletePayload): void {
  emit(el, SYSML_EVENTS.INGESTION_COMPLETE, payload);
}

export function emitIngestionError(el: HTMLElement, payload: SysmlIngestionErrorPayload): void {
  emit(el, SYSML_EVENTS.INGESTION_ERROR, payload);
}

// ── Diagram interaction events ─────────────────────────────────────────────

/** Payload for node context menu events */
export interface SysmlNodeContextMenuPayload {
  nodeId: string;
  x: number;
  y: number;
}

/** Custom event fired when a node is right-clicked in the diagram */
export type SysmlNodeContextMenuEvent = CustomEvent<SysmlNodeContextMenuPayload>;

/** Payload for diagram export events */
export interface SysmlDiagramExportPayload {
  format: 'svg' | 'png';
}

/** Custom event fired when the user requests a diagram export */
export type SysmlDiagramExportEvent = CustomEvent<SysmlDiagramExportPayload>;

/** Payload for layout change events */
export interface SysmlLayoutChangePayload {
  algorithm?: string;
  direction?: string;
  nodeSpacing?: number;
  layerSpacing?: number;
  edgeRouting?: string;
}

/** Custom event fired when layout options change */
export type SysmlLayoutChangeEvent = CustomEvent<SysmlLayoutChangePayload>;
